import { useState, useEffect, useRef, useCallback } from 'react';
import { bookingApi, type BookingOrgProfile, type EstimateResponse } from '../lib/bookingApi';
import type { DiscoverySource } from '@iomi/shared';

// ── Types ────────────────────────────────────────────────────────────────────

export interface AddressValue {
  address: string;
  placeId: string;
}

export interface BookingFormState {
  // Move type
  type: 'residential' | 'commercial' | '';
  // Contact
  name: string;
  email: string;
  phone: string;
  businessName: string;
  discoverySource: DiscoverySource | '';
  referredBy: string;
  // Move details
  preferredMoveDate: string;
  estimatedMoveDateRange: string;
  flexibleDate: boolean;
  originAddress: AddressValue | null;
  originZipCode: string;
  destinationAddress: AddressValue | null;
  destinationZipCode: string;
  numberOfEmployees: string;
  squareFootage: string;
  bedrooms: string;
  // Additional details (residential)
  originFlightsOfStairs: string;
  destinationFlightsOfStairs: string;
  elevatorAccess: boolean;
  longCarry: boolean;
  // Specialty items
  hasSpecialtyItems: boolean;
  specialtyItemsDetails: string;
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

const INITIAL_STATE: BookingFormState = {
  type: '',
  name: '',
  email: '',
  phone: '',
  businessName: '',
  discoverySource: '',
  referredBy: '',
  preferredMoveDate: '',
  estimatedMoveDateRange: '',
  flexibleDate: false,
  originAddress: null,
  originZipCode: '',
  destinationAddress: null,
  destinationZipCode: '',
  numberOfEmployees: '',
  squareFootage: '',
  bedrooms: '',
  originFlightsOfStairs: '',
  destinationFlightsOfStairs: '',
  elevatorAccess: false,
  longCarry: false,
  hasSpecialtyItems: false,
  specialtyItemsDetails: '',
};

// ── Helpers ─────────────────────────────────────────────────────────────────

/** Build a PATCH body from the current form state. */
function buildPatchBody(f: BookingFormState): Record<string, unknown> {
  const body: Record<string, unknown> = {};

  if (f.name.trim()) body.name = f.name.trim();
  if (f.email.trim()) body.email = f.email.trim();
  if (f.phone.trim()) body.phone = f.phone.trim();
  if (f.businessName.trim()) body.businessName = f.businessName.trim();
  if (f.discoverySource) body.discoverySource = f.discoverySource;
  if (f.referredBy.trim()) body.referredBy = f.referredBy.trim();
  if (f.preferredMoveDate) body.preferredMoveDate = f.preferredMoveDate;
  if (f.estimatedMoveDateRange.trim()) body.estimatedMoveDateRange = f.estimatedMoveDateRange.trim();
  body.flexibleDate = f.flexibleDate;
  if (f.originAddress) body.originAddress = f.originAddress;
  if (f.originZipCode.trim()) body.originZipCode = f.originZipCode.trim();
  if (f.destinationAddress) body.destinationAddress = f.destinationAddress;
  if (f.destinationZipCode.trim()) body.destinationZipCode = f.destinationZipCode.trim();
  if (f.numberOfEmployees) body.numberOfEmployees = Number(f.numberOfEmployees);
  if (f.squareFootage) body.squareFootage = Number(f.squareFootage);
  if (f.bedrooms) body.bedrooms = Number(f.bedrooms);
  if (f.originFlightsOfStairs) body.originFlightsOfStairs = Number(f.originFlightsOfStairs);
  if (f.destinationFlightsOfStairs) body.destinationFlightsOfStairs = Number(f.destinationFlightsOfStairs);
  body.elevatorAccess = f.elevatorAccess;
  body.longCarry = f.longCarry;
  body.hasSpecialtyItems = f.hasSpecialtyItems;
  if (f.specialtyItemsDetails.trim()) body.specialtyItemsDetails = f.specialtyItemsDetails.trim();
  if (f.type) body.type = f.type;

  return body;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useBookingForm(slug: string) {
  const [orgProfile, setOrgProfile] = useState<BookingOrgProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [orgError, setOrgError] = useState('');
  const [form, setForm] = useState<BookingFormState>(INITIAL_STATE);
  const [bookingToken, setBookingToken] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [estimating, setEstimating] = useState(false);

  // Keep a ref to always have the latest form state for async callbacks
  const formRef = useRef<BookingFormState>(form);
  formRef.current = form;

  // ── Load org profile ───────────────────────────────────────────────────

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await bookingApi.getOrgProfile(slug);
        if (!cancelled) {
          setOrgProfile(res.data);
          // Auto-set type if only one is enabled
          if (res.data.acceptsCommercial && !res.data.acceptsResidential) {
            setForm(prev => ({ ...prev, type: 'commercial' }));
          } else if (res.data.acceptsResidential && !res.data.acceptsCommercial) {
            setForm(prev => ({ ...prev, type: 'residential' }));
          }
        }
      } catch (err) {
        if (!cancelled) setOrgError((err as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [slug]);

  // ── Update a form field ────────────────────────────────────────────────

  const updateField = useCallback(<K extends keyof BookingFormState>(
    field: K,
    value: BookingFormState[K],
  ) => {
    setForm(prev => ({ ...prev, [field]: value }));
  }, []);

  // ── Section completion ─────────────────────────────────────────────────

  const effectiveType = form.type || (orgProfile?.acceptsResidential && !orgProfile?.acceptsCommercial ? 'residential' : orgProfile?.acceptsCommercial && !orgProfile?.acceptsResidential ? 'commercial' : '');

  const isMoveTypeComplete = !!effectiveType;

  const isContactComplete = !!form.name.trim() && (!!form.email.trim() || !!form.phone.trim());

  const isMoveDetailsComplete = (() => {
    if (!effectiveType) return false;
    const hasDate = !!form.preferredMoveDate || !!form.estimatedMoveDateRange.trim();
    const hasOrigin = !!form.originAddress || !!form.originZipCode.trim();
    const hasDest = !!form.destinationAddress || !!form.destinationZipCode.trim();
    if (!hasDate || !hasOrigin || !hasDest) return false;
    if (effectiveType === 'commercial') {
      return !!form.numberOfEmployees;
    }
    // residential
    return !!form.bedrooms;
  })();

  const isFormComplete = isMoveTypeComplete && isContactComplete && isMoveDetailsComplete;

  // ── Submit form (create lead + patch all data) ─────────────────────────

  const submitForm = useCallback(async (): Promise<string | null> => {
    const current = formRef.current;
    if (!current.email.trim() && !current.phone.trim()) return null;

    // If already submitted, return existing token
    if (bookingToken) return bookingToken;

    try {
      setSubmitting(true);

      // 1. Create the lead
      const res = await bookingApi.createLead(slug, {
        email: current.email.trim() || undefined,
        phone: current.phone.trim() || undefined,
        name: current.name.trim() || undefined,
        type: current.type || undefined,
      });
      const token = res.data.bookingToken;
      setBookingToken(token);

      // 2. Immediately patch with all form data
      const patchBody = buildPatchBody(current);
      await bookingApi.updateLead(slug, token, patchBody);

      return token;
    } catch {
      return null;
    } finally {
      setSubmitting(false);
    }
  }, [bookingToken, slug]);

  // ── Calculate estimate (submits form first if needed) ──────────────────

  const calculateEstimate = useCallback(async (): Promise<EstimateResponse | null> => {
    const token = await submitForm();
    if (!token) return null;

    try {
      setEstimating(true);
      const res = await bookingApi.calculateEstimate(slug, token);
      return res.data;
    } catch {
      return null;
    } finally {
      setEstimating(false);
    }
  }, [slug, submitForm]);

  return {
    // Org
    orgProfile,
    loading,
    orgError,
    // Form
    form,
    updateField,
    effectiveType,
    // Submit
    bookingToken,
    submitting,
    submitForm,
    // Sections
    isMoveTypeComplete,
    isContactComplete,
    isMoveDetailsComplete,
    isFormComplete,
    // Estimate
    estimating,
    calculateEstimate,
  };
}
